// InvestorManagement.js - Enhanced with Invitation Workflow & Fee Parameters
// Super Admin component for managing impact investors

import React, { useState, useEffect } from 'react';
import {
  Landmark, Plus, Search, RefreshCw, X, CheckCircle, Clock, Send, Percent, Copy
} from 'lucide-react';
import { invitationsApi } from '../../../services/invitationsApi';
import { getStatusStyle } from '../../../config/statusColors';

const COUNTRIES = [
  { code: 'US', name: 'USA' }, { code: 'GB', name: 'UK' }, { code: 'CH', name: 'Switzerland' },
  { code: 'NL', name: 'Netherlands' }, { code: 'NO', name: 'Norway' }, { code: 'SE', name: 'Sweden' },
  { code: 'JP', name: 'Japan' }, { code: 'SG', name: 'Singapore' }, { code: 'DE', name: 'Germany' },
];

const INVESTOR_TYPES = [
  { value: 'institutional', label: 'Institutional (Pension/Sovereign)' },
  { value: 'foundation', label: 'Foundation / Endowment' },
  { value: 'family_office', label: 'Family Office' },
  { value: 'development_finance', label: 'Development Finance Institution' },
  { value: 'impact_fund', label: 'Impact Investment Fund' },
];

const RISK_PROFILES = [
  { value: 'conservative', label: 'Conservative', desc: 'Lower yields, max protection' },
  { value: 'moderate', label: 'Moderate', desc: 'Balanced risk-return' },
  { value: 'aggressive', label: 'Aggressive', desc: 'Higher yields, accepts volatility' },
];

const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizeClasses = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white rounded-xl shadow-xl ${sizeClasses[size]} w-full mx-4 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => {
  const colors = { emerald: 'bg-gray-100 text-gray-600', green: 'bg-gray-100 text-gray-600', blue: 'bg-gray-100 text-gray-600', amber: 'bg-gray-100 text-gray-600' };
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${colors[color]}`}><Icon className="w-6 h-6" /></div>
      </div>
    </div>
  );
};

const InvestorManagement = () => {
  const [investors] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('investors');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', organizationName: '', country: 'USA', city: '',
    investorType: 'institutional', riskTolerance: 'moderate',
    managementFee: 0.5, performanceFee: 10.0, hurdleRate: 8.0,
  });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await invitationsApi.list({ type: 'tenant_investor' });
      setInvitations(data);
    } catch (error) { console.error('Failed to load:', error); }
    finally { setLoading(false); }
  };

  const handleInviteInvestor = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      const result = await invitationsApi.inviteInvestor(formData);
      setInviteSuccess({ email: result.email, organizationName: result.organizationName, tempPassword: result.tempPassword });
      loadData();
      setFormData({ firstName: '', lastName: '', email: '', organizationName: '', country: 'USA', city: '',
        investorType: 'institutional', riskTolerance: 'moderate', managementFee: 0.5, performanceFee: 10.0, hurdleRate: 8.0 });
    } catch (error) { setFormError(error.message); }
    finally { setSubmitting(false); }
  };

  const handleResend = async (id) => { try { await invitationsApi.resend(id); loadData(); } catch (e) { alert('Failed: ' + e.message); } };
  const handleCancel = async (id) => { if (!window.confirm('Cancel?')) return; try { await invitationsApi.cancel(id); loadData(); } catch (e) { alert('Failed: ' + e.message); } };

  const pendingInvitations = invitations.filter(i => ['pending', 'email_sent'].includes(i.status)).length;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Investor Management</h1>
          <p className="text-gray-600 mt-1">Manage impact investors and fee structures</p>
        </div>
        <button onClick={() => { setShowInviteModal(true); setInviteSuccess(null); }} className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
          <Plus className="w-4 h-4" />Invite Investor
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Total Investors" value={investors.length} icon={Landmark} color="emerald" />
        <StatCard title="Pending Invitations" value={pendingInvitations} icon={Clock} color="amber" />
        <StatCard title="Active Investors" value={investors.filter(i => i.status === 'active').length} icon={CheckCircle} color="green" />
        <StatCard title="Total Invitations" value={invitations.length} icon={Send} color="blue" />
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setActiveTab('investors')} className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'investors' ? 'bg-gray-600 text-white' : 'bg-white text-gray-600'}`}>Investors ({investors.length})</button>
        <button onClick={() => setActiveTab('invitations')} className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'invitations' ? 'bg-gray-600 text-white' : 'bg-white text-gray-600'}`}>Invitations ({invitations.length})</button>
      </div>

      {activeTab === 'invitations' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100"><h3 className="text-lg font-semibold text-gray-900">Investor Invitations</h3></div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invitations.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No invitations yet.</td></tr>
              ) : invitations.map(inv => (
                <tr key={inv.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center"><Landmark className="w-5 h-5 text-gray-600" /></div>
                      <div><p className="font-medium text-gray-900">{inv.organization_name}</p><p className="text-xs text-gray-500">{inv.email}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-gray-900">{inv.recipient_name}</td>
                  <td className="px-4 py-4"><span className="px-2 py-1 rounded-full text-xs font-medium" style={getStatusStyle(inv.status)}>{inv.status_label || inv.status}</span></td>
                  <td className="px-4 py-4 text-gray-600">{new Date(inv.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-4">
                    {['pending', 'email_sent'].includes(inv.status) && (
                      <div className="flex gap-1">
                        <button onClick={() => handleResend(inv.id)} className="p-1.5 text-gray-600 hover:bg-gray-50 rounded-lg"><RefreshCw className="w-4 h-4" /></button>
                        <button onClick={() => handleCancel(inv.id)} className="p-1.5 text-gray-600 hover:bg-gray-50 rounded-lg"><X className="w-4 h-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'investors' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-gray-500">
          <Landmark className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>Investors appear here once invitations are confirmed.</p>
          <button onClick={() => setActiveTab('invitations')} className="mt-4 text-gray-600 hover:underline">View Invitations →</button>
        </div>
      )}

      <Modal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} title="Invite Impact Investor" size="lg">
        {inviteSuccess ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-8 h-8 text-gray-600" /></div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Invitation Sent!</h3>
            <p className="text-gray-600 mb-4">Email sent to <strong>{inviteSuccess.email}</strong> for <strong>{inviteSuccess.organizationName}</strong>.</p>
            {inviteSuccess.tempPassword && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 text-left">
                <p className="text-sm text-gray-800 font-medium mb-2">🔧 Dev Mode Credentials:</p>
                <div className="flex items-center justify-between bg-white p-2 rounded border">
                  <code className="text-sm">{inviteSuccess.tempPassword}</code>
                  <button onClick={() => navigator.clipboard.writeText(inviteSuccess.tempPassword)} className="p-1 hover:bg-gray-100 rounded"><Copy className="w-4 h-4" /></button>
                </div>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <button onClick={() => { setShowInviteModal(false); setInviteSuccess(null); }} className="px-4 py-2 border rounded-lg">Close</button>
              <button onClick={() => setInviteSuccess(null)} className="px-4 py-2 bg-gray-600 text-white rounded-lg">Invite Another</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleInviteInvestor} className="space-y-6">
            {formError && <div className="bg-gray-50 border border-gray-200 text-gray-700 px-4 py-3 rounded-lg">{formError}</div>}
            
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Investor Admin Contact</h4>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input type="text" required value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} className="w-full px-4 py-2 border rounded-lg" placeholder="Sarah" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input type="text" required value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} className="w-full px-4 py-2 border rounded-lg" placeholder="Johnson" /></div>
              </div>
              <div className="mt-4"><label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-2 border rounded-lg" placeholder="invest@fund.com" /></div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Organization</h4>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input type="text" required value={formData.organizationName} onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })} className="w-full px-4 py-2 border rounded-lg" placeholder="Nordic Impact Fund" /></div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                  <select required value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="w-full px-4 py-2 border rounded-lg">
                    {COUNTRIES.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="w-full px-4 py-2 border rounded-lg" placeholder="Oslo" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select value={formData.investorType} onChange={(e) => setFormData({ ...formData, investorType: e.target.value })} className="w-full px-4 py-2 border rounded-lg">
                    {INVESTOR_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Risk Profile *</label>
                  <select value={formData.riskTolerance} onChange={(e) => setFormData({ ...formData, riskTolerance: e.target.value })} className="w-full px-4 py-2 border rounded-lg">
                    {RISK_PROFILES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">{RISK_PROFILES.find(r => r.value === formData.riskTolerance)?.desc}</p></div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3 flex items-center gap-2"><Percent className="w-4 h-4" />Fee Structure</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Management (%/yr)</label>
                    <input type="number" step="0.1" min="0" max="5" value={formData.managementFee} onChange={(e) => setFormData({ ...formData, managementFee: parseFloat(e.target.value) })} className="w-full px-4 py-2 border rounded-lg" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Performance (%)</label>
                    <input type="number" step="0.5" min="0" max="30" value={formData.performanceFee} onChange={(e) => setFormData({ ...formData, performanceFee: parseFloat(e.target.value) })} className="w-full px-4 py-2 border rounded-lg" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Hurdle Rate (%)</label>
                    <input type="number" step="0.5" min="0" max="20" value={formData.hurdleRate} onChange={(e) => setFormData({ ...formData, hurdleRate: parseFloat(e.target.value) })} className="w-full px-4 py-2 border rounded-lg" /></div>
                </div>
                <div className="bg-white rounded-lg p-3 border mt-4 text-sm text-gray-600">
                  <p>• $10M invested → <strong>${(10000000 * formData.managementFee / 100).toLocaleString()}/yr</strong> management</p>
                  <p>• Returns above {formData.hurdleRate}% → <strong>{formData.performanceFee}%</strong> performance fee</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <button type="button" onClick={() => setShowInviteModal(false)} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={submitting} className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {submitting ? <><RefreshCw className="w-4 h-4 animate-spin" />Sending...</> : <><Send className="w-4 h-4" />Send Invitation</>}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default InvestorManagement;
